/**
 * The profile hero: avatar initials, name, and a tier pill with this season's points.
 *
 * The block itself reads GET /api/users/me through the provider; the only thing the host owns is
 * the team branding, which no loyalty endpoint serves.
 *
 * Branding comes from the same session read the join block uses. `useLoyaltySession` re-reads on
 * every change of the fan, so a sign-in landing while this page is open — and one fan replacing
 * another — both repaint the crest without a reload.
 */

import { LoyaltyHero } from '@drewling/urc-loyalty-ui';
import { useLoyaltySession } from '../useLoyaltySession';
// Reserves the band's height on the empty mount container, so the wait below costs no layout shift.
// The heights and why they are what they are are in the file itself.
import '../profileHeader.css';

export function ProfileHeaderBlock() {
	const session = useLoyaltySession();

	// `undefined` means the answer has not landed. Rendering the band unbranded and then repainting
	// it in the club's colour is a visible flash, so wait instead; the block draws its own skeleton
	// for the profile read, which lands in the same window. The wait is now until the SDK settles
	// rather than a round trip -- a few hundred milliseconds on a cold page, a microtask on a warm
	// cache -- which is precisely why `session.js` waits on `ready` rather than reading `state()`,
	// whose `null` would be indistinguishable from "answered, no club".
	//
	// Returning nothing is free in layout terms only because profileHeader.css holds the band's
	// height open on the mount container from the first paint. Without it this branch is a 0-height
	// box that jumps to 312 (304 mobile) when the request lands, shoving the whole page down.
	if ( session === undefined ) {
		return null;
	}

	// `null` is a supported state, not a missing one: LoyaltyHero draws an unbranded band for a
	// fan with no club. That is the right treatment here — unlike the join dialog, which cannot
	// take a null team and stands the URC's own mark in (D-043).
	return <LoyaltyHero headingLevel={ 1 } team={ session.team } />;
}
